import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/base/MovieCard';
import { moviesData, categories } from '@/mocks/movies';

export default function Home() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const hotMovies = moviesData.filter(m => m.isHot).slice(0, 10);
  const latestMovies = moviesData.filter(m => m.isNew).slice(0, 10);
  const topRated = [...moviesData].sort((a, b) => b.rating - a.rating).slice(0, 10);
  const featuredSlides = moviesData.filter(m => m.isHot).slice(0, 5);

  const faqItems = [
    { q: '97影视真的完全免费吗？', a: '是的，97影视所有内容均完全免费，无需注册、无需会员，打开即看。我们致力于为影迷提供优质的免费观影服务。' },
    { q: '观看视频需要安装播放器吗？', a: '不需要。97影视采用HTML5播放技术，直接在浏览器中播放，无需下载或安装任何第三方播放器或插件。' },
    { q: '为什么选择97影视而不是其他平台？', a: '97影视拥有海量片库、极速秒播、纯净无广告三大核心优势。我们专注观影体验，不做任何干扰用户观看的事情。' },
    { q: '支持哪些设备观看？', a: '97影视全面支持电脑、平板、手机等所有主流设备，自动适配不同屏幕尺寸，随时随地畅享高清观影体验。' },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % featuredSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [featuredSlides.length]);

  const schemaOrg = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "97影视",
    "url": typeof window !== 'undefined' ? window.location.origin : '',
    "description": "97影视提供高清免费在线观看服务，海量电影电视剧全集免费看，流畅不卡顿秒播无广告，极速观影体验。",
    "potentialAction": {
      "@type": "SearchAction",
      "target": `${typeof window !== 'undefined' ? window.location.origin : ''}/search?q={search_term_string}`,
      "query-input": "required name=search_term_string"
    }
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaOrg) }}
      />

      <header>
        <Navbar />
      </header>

      <main>
        {/* Hero Section */}
        <section className="relative h-[420px] md:h-[600px] overflow-hidden">
          {featuredSlides.map((slide, index) => (
            <div
              key={slide.id}
              className={`absolute inset-0 transition-opacity duration-700 ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={slide.imageUrl.replace('width=600&height=800', 'width=1600&height=900')}
                alt={slide.title}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/70"></div>
            </div>
          ))}

          <div className="absolute inset-0 flex items-center">
            <div className="w-full px-4 md:px-6">
              <div className="max-w-2xl">
                <h1 className="text-3xl md:text-5xl font-bold text-background-50 font-heading mb-4 leading-tight">
                  {featuredSlides[currentSlide]?.title}
                </h1>
                <p className="text-background-200 text-sm md:text-base mb-3 line-clamp-2">
                  {featuredSlides[currentSlide]?.description}
                </p>
                <div className="flex items-center gap-4 mb-6">
                  <span className="flex items-center gap-1 text-accent-400 text-sm">
                    <i className="ri-star-fill"></i>
                    <strong>{featuredSlides[currentSlide]?.rating}</strong>
                  </span>
                  <span className="text-background-300 text-sm">{featuredSlides[currentSlide]?.year}</span>
                  <span className="bg-background-50/20 text-background-200 px-2 py-0.5 rounded text-xs whitespace-nowrap">
                    {featuredSlides[currentSlide]?.subCategory}
                  </span>
                </div>
                <Link
                  to={`/article/${featuredSlides[currentSlide]?.id}`}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-lg font-medium text-sm transition-all hover:bg-primary-600 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-play-circle-fill text-lg"></i>
                  立即观看
                </Link>
              </div>
            </div>
          </div>

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
            {featuredSlides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
                  index === currentSlide ? 'bg-primary-500 w-6' : 'bg-background-50/50 hover:bg-background-50/80'
                }`}
                aria-label={`切换到第${index + 1}张幻灯片`}
              />
            ))}
          </div>
        </section>

        {/* Category Navigation */}
        <section className="bg-background-50 py-6 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center gap-3 overflow-x-auto">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex items-center gap-2 px-5 py-3 bg-background-100 hover:bg-primary-100 rounded-lg transition-colors whitespace-nowrap cursor-pointer group"
                >
                  <div className="w-8 h-8 flex items-center justify-center">
                    <i className={`${cat.icon} text-lg text-primary-500 group-hover:text-primary-600`}></i>
                  </div>
                  <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-600">{cat.name}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Hot Movies */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">热门推荐</h2>
                <span className="px-2 py-0.5 bg-primary-100 text-primary-700 text-xs rounded-full font-medium whitespace-nowrap">HOT</span>
              </div>
              <Link to="/category/movie" className="text-sm text-foreground-500 hover:text-primary-500 transition-colors flex items-center gap-1 whitespace-nowrap cursor-pointer">
                查看更多 <i className="ri-arrow-right-line text-xs"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop>
              {hotMovies.map((movie) => (
                <MovieCard key={movie.id} {...movie} />
              ))}
            </div>
          </div>
        </section>

        {/* Stats Banner */}
        <section className="py-10 md:py-14 bg-background-900">
          <div className="w-full px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div>
                <div className="text-3xl md:text-4xl font-bold text-accent-500 font-heading">50万+</div>
                <p className="text-background-400 text-sm mt-2">影视资源</p>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-bold text-primary-400 font-heading">1000万+</div>
                <p className="text-background-400 text-sm mt-2">忠实用户</p>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-bold text-accent-500 font-heading">99.9%</div>
                <p className="text-background-400 text-sm mt-2">播放成功率</p>
              </div>
              <div>
                <div className="text-3xl md:text-4xl font-bold text-primary-400 font-heading">0</div>
                <p className="text-background-400 text-sm mt-2">广告打扰</p>
              </div>
            </div>
          </div>
        </section>

        {/* Top Rated */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">高分榜单</h2>
                <span className="px-2 py-0.5 bg-accent-100 text-accent-700 text-xs rounded-full font-medium whitespace-nowrap">TOP 10</span>
              </div>
              <Link to="/category/movie" className="text-sm text-foreground-500 hover:text-primary-500 transition-colors flex items-center gap-1 whitespace-nowrap cursor-pointer">
                查看更多 <i className="ri-arrow-right-line text-xs"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop>
              {topRated.map((movie) => (
                <MovieCard key={movie.id} {...movie} />
              ))}
            </div>
          </div>
        </section>

        {/* Latest Updates */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">最新上线</h2>
                <span className="px-2 py-0.5 bg-secondary-100 text-secondary-700 text-xs rounded-full font-medium whitespace-nowrap">NEW</span>
              </div>
              <Link to="/category/tv" className="text-sm text-foreground-500 hover:text-primary-500 transition-colors flex items-center gap-1 whitespace-nowrap cursor-pointer">
                查看更多 <i className="ri-arrow-right-line text-xs"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop>
              {latestMovies.map((movie) => (
                <MovieCard key={movie.id} {...movie} />
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading text-center mb-8">为什么选择97影视</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-background-50 rounded-lg p-6 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center bg-primary-100 rounded-full">
                  <i className="ri-movie-2-line text-2xl text-primary-500"></i>
                </div>
                <h3 className="font-bold text-foreground-900 mb-2 font-heading">海量片库</h3>
                <p className="text-sm text-foreground-600">涵盖电影、电视剧、综艺、动漫等各类资源，满足您的所有观影需求。</p>
              </div>
              <div className="bg-background-50 rounded-lg p-6 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center bg-accent-100 rounded-full">
                  <i className="ri-speed-up-line text-2xl text-accent-500"></i>
                </div>
                <h3 className="font-bold text-foreground-900 mb-2 font-heading">极速秒播</h3>
                <p className="text-sm text-foreground-600">采用先进CDN加速技术，确保高清视频流畅播放，告别卡顿困扰。</p>
              </div>
              <div className="bg-background-50 rounded-lg p-6 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center bg-secondary-100 rounded-full">
                  <i className="ri-close-circle-line text-2xl text-secondary-500"></i>
                </div>
                <h3 className="font-bold text-foreground-900 mb-2 font-heading">纯净无广告</h3>
                <p className="text-sm text-foreground-600">零广告打扰，专注观影体验，让您沉浸在影视世界中不受干扰。</p>
              </div>
              <div className="bg-background-50 rounded-lg p-6 text-center">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center bg-primary-100 rounded-full">
                  <i className="ri-device-line text-2xl text-primary-500"></i>
                </div>
                <h3 className="font-bold text-foreground-900 mb-2 font-heading">多端支持</h3>
                <p className="text-sm text-foreground-600">电脑、平板、手机全终端适配，随时随地享受高清观影体验。</p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading text-center mb-8">常见问题</h2>
            <div className="space-y-3">
              {faqItems.map((faq, index) => (
                <details key={index} className="group bg-background-100 rounded-lg">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer font-medium text-foreground-800 text-sm list-none">
                    {faq.q}
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:hidden w-5 h-5 flex items-center justify-center"></i>
                    <i className="ri-arrow-up-s-line text-foreground-400 hidden group-open:block w-5 h-5 flex items-center justify-center"></i>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {/* Organization Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "97影视",
            "url": typeof window !== 'undefined' ? window.location.origin : '',
            "description": "97影视提供高清免费在线观看服务，海量电影电视剧全集免费看，流畅不卡顿秒播无广告。",
            "foundingDate": "2020",
          })
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              { "@type": "Question", "name": "97影视真的完全免费吗？", "acceptedAnswer": { "@type": "Answer", "text": "是的，97影视所有内容均完全免费，无需注册、无需会员，打开即看。我们致力于为影迷提供优质的免费观影服务。" } },
              { "@type": "Question", "name": "观看视频需要安装播放器吗？", "acceptedAnswer": { "@type": "Answer", "text": "不需要。97影视采用HTML5播放技术，直接在浏览器中播放，无需下载或安装任何第三方播放器或插件。" } },
              { "@type": "Question", "name": "为什么选择97影视而不是其他平台？", "acceptedAnswer": { "@type": "Answer", "text": "97影视拥有海量片库、极速秒播、纯净无广告三大核心优势。我们专注观影体验，不做任何干扰用户观看的事情。" } },
              { "@type": "Question", "name": "支持哪些设备观看？", "acceptedAnswer": { "@type": "Answer", "text": "97影视全面支持电脑、平板、手机等所有主流设备，自动适配不同屏幕尺寸，随时随地畅享高清观影体验。" } },
            ]
          })
        }}
      />
    </>
  );
}